import type { DateArg } from "date-fns";
import type { Dispatch, SetStateAction } from "react";
import { StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import {
    DateTimePicker,
    type DateTimePickerProps,
    type DateTimeRenderProps,
} from "~/components/form/date-picker/DateTimePicker";
import {
    formatApproximateDateForHumans,
    formatDateTimeStartOfSecond,
    formatTimeForHumans,
    type Mode,
} from "~/util/dates";

type JournalTimePickerProps = {
    value: DateArg<Date>;
    onDateSelected: ((value: DateArg<Date>) => void) | Dispatch<SetStateAction<DateArg<Date>>>;
} & Omit<DateTimePickerProps, "value" | "renderValue" | "onDateSelected">;

export const JournalTimePicker = ({
    value,
    onDateSelected,
    mode,
    max,
    displayFormatter,
    displayDateMode = "approximate",
    ...pickerProps
}: JournalTimePickerProps) => {
    const onSelected = (date: DateArg<Date> | null) => {
        onDateSelected?.(formatDateTimeStartOfSecond(date ?? value));
    };

    const render = ({ disabled, onPress, date, formatter, mode }: DateTimeRenderProps) => {
        return (
            <Button
                disabled={disabled}
                onPress={onPress}
                title={formatter(date ?? value, mode)}
                icon={mode === "date" ? "calendar" : "clock"}
                variant="neutral"
                size="sm"
            />
        );
    };

    const format = (date: DateArg<Date>, mode: Mode) => {
        switch (mode) {
            case "date":
                return formatApproximateDateForHumans(date);
            case "time":
                return formatTimeForHumans(date);
            default:
                return `${formatApproximateDateForHumans(date, true)}, ${formatTimeForHumans(date)}`;
        }
    };

    return (
        <View style={styles.container}>
            <DateTimePicker
                {...pickerProps}
                mode={mode}
                value={value}
                onDateSelected={onSelected}
                renderValue={render}
                displayFormatter={displayFormatter ?? format}
                displayDateMode={displayDateMode}
                max={max ?? new Date()}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: "flex-start",
    },
});
